import { useEffect } from "react";
import { Link } from "react-router-dom";
import { routesMap } from "@/components/routes/routes";
import { doLogin } from "@/services/service-layer/authService";
import OpticksButton from "@/components/ui/OpticksButton/OpticksButton";
import OpticksInput from "@/components/ui/OpticksInput/OpticksInput";
import OpticksLink from "@/components/ui/OpticksLink/OpticksLink";
import type { LoginFormPayload } from "@/types/model/Auth";
import { Controller, useForm } from "react-hook-form";
import { useNotificationStore } from "@/zustand/notificationsStore";
import { useLoaderStore } from "@/zustand/loaderStore";
import OpticksLogo from "@/components/ui/OpticksLogos/OpticksLogo";
import GoogleAuthButton from "@/components/ui/GoogleAuthButton/GoogleAuthButton";
import "./Login.css";

interface HandleLoginValues extends LoginFormPayload {}

const Login = () => {
  const params = new URLSearchParams(location.search);
  const { loading, setLoading } = useLoaderStore((state) => state);
  const handleLogin = (values: HandleLoginValues) => {
    setLoading(true);
    doLogin(values);
  };

  const { success } = useNotificationStore((state) => state);

  useEffect(() => {
    if (
      (params.get("verified")?.length as number) > 0 &&
      params.get("verified") === "true"
    ) {
      success("Your account has been created successfully");
    }
  }, []);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<HandleLoginValues>({
    mode: "onSubmit",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  return (
    <>
      <h2>
        Log in to <OpticksLogo />
      </h2>
      <form
        className="auth-form"
        onSubmit={handleSubmit(handleLogin)}
        noValidate={true}
      >
        <div className="fields">
          <Controller
            name="email"
            rules={{
              required: "Required",
              validate: (value) => /\S+@\S+\.\S+/.test(value) || "Invalid",
            }}
            control={control}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="Email"
                  type="email"
                  required
                  surface="white"
                  input={field}
                />
                {errors.email && (
                  <span className="form-invalid-message">
                    {errors.email.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="password"
            rules={{
              required: "Required",
            }}
            control={control}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="Password"
                  type="password"
                  required
                  surface="white"
                  input={field}
                />
                {errors.password && (
                  <span className="form-invalid-message">
                    {errors.password.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        <div className="forgot-password">
          <Link to={routesMap.auth.forgotPassword.path}>Forgot password?</Link>
        </div>

        <div className="login-btn">
          <OpticksButton
            type="submit"
            size="m-wide"
            disabled={!isValid || isSubmitting || loading}
            label="Log In"
          />
        </div>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <GoogleAuthButton text="signin_with" />

        <div className="create-account">
          <span>
            Have any questions?{" "}
            <OpticksLink
              href="https://optickssecurity.com/contact-us"
              label="Contact Us"
            />
          </span>
        </div>
      </form>
    </>
  );
};

export default Login;
